import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../../../helpers/acl/permissions';
import { message } from 'antd';
import { messageI18n, tToast } from '../../../helpers/i18n/toastI18n';
import {
  Monitor,
  RefreshCw,
  Settings,
  ChevronDown,
  Server,
  Wifi,
  Power,
  PowerOff,
  RotateCcw,
  Upload,
  Copy,
  Cable,
  Trash2,
  MoreHorizontal,
  Plus,
} from 'lucide-react';

import { Button, Dropdown, DropdownItem } from './custom-components';

import {
  ConfigureAEModal,
  CloneDeviceModal,
  ConnectionCreationModal,
  ConfigureDeviceModal,
  CertificateManagementModal,
  DeleteConfirmationModal,
  AEViewModal,
  AcceptedCallingAETitlesModal,
} from './modals';

import DeviceCreationForm from './device-creation-form';
import DeviceCreationOptimalForm from './device-creation-optimal';

import type {
  Device,
  Connection,
  ApplicationEntity,
  DeviceStatus,
  AEStatus,
} from './types';

import {
  getDeviceStatusBadge,
  getAEStatusBadge,
  getSecureBadge,
} from './utils/status-badges';
import { defaultSyntaxConfig } from './resources/dicom-config';
import { transformBackendSyntaxConfig } from './utils/syntax-transformer';
import { useDeviceManagement } from './hooks/use-device-management';
import {
  useGetDevices,
  useCreateDeviceWithTls,
  useCreateDeviceWithPemTls,
  useCreateDeviceWithPfxTls,
  useStartDevice,
  useStopDevice,
  useRestartDevice,
  downloadCertificateBundle,
  downloadClientKey,
  downloadClientCertificate,
  downloadCACertificate,
  useDeleteDevice,
  useDeleteConnection,
  useDeleteApplicationEntity,
  useStartApplicationEntity,
  useStopApplicationEntity,
  useStopAllConnectionAes,
  useStartAllConnectionAes,
  useRestartAllConnectionAes,
  useUpdateDevice,
  useGetDevicesWithConnections,
  useGetApplicationEntitiesPageable,
} from '../../../helpers/api/pacs/pacs';

import { Modal } from '../../ComponnentsUI/common/Modal';
import ApplicationEntitiesTable from './application-entities-table';

const mapDeviceStatus = (backendStatus: string): DeviceStatus => {
  switch (backendStatus.toUpperCase()) {
    case 'ACTIVE':
      return 'active';
    case 'INACTIVE':
      return 'inactive';
    default:
      return 'inactive';
  }
};

const mapAEStatus = (backendStatus: string): AEStatus => {
  switch (backendStatus.toUpperCase()) {
    case 'ACTIVE':
      return 'running';
    case 'INACTIVE':
      return 'stopped';
    default:
      return 'stopped';
  }
};

const transformBackendDevices = (backendDevices: any[]): Device[] => {
  if (!backendDevices || !Array.isArray(backendDevices)) {
    return [];
  }

  return backendDevices.map((backendDevice) => ({
    id: backendDevice.id.toString(),
    name: backendDevice.deviceName,
    description: backendDevice.description,
    manufacturer: backendDevice.manufacturer,
    model: backendDevice.manufacturerModelName,
    stationName: backendDevice.stationName,
    institutionName: backendDevice.institutionName,
    institutionalDepartmentName: backendDevice.institutionalDepartmentName,
    status: mapDeviceStatus(backendDevice.status),
    createdAt: backendDevice.createdAt,
    lastUpdated: backendDevice.updatedAt,
    connections:
      backendDevice.connections?.map((backendConnection: any) => ({
        id: backendConnection.id.toString(),
        name: backendConnection.connectionName,
        hostname: backendConnection.hostname,
        port: backendConnection.port,
        protocol: 'TCP',
        description: backendConnection.description,
        tlsEnabled: backendConnection.tlsEnabled,
        tlsNeedClientAuth: backendConnection.tlsNeedClientAuth,

        applicationEntities:
          backendConnection.applicationEntities?.map((backendAE: any) => ({
            id: backendAE.id.toString(),
            aeTitle: backendAE.aeTitle,
            description: backendAE.description,
            dicomType: backendAE.aeType
              ? backendAE.aeType.toLowerCase() === 'blind'
                ? 'blind'
                : 'standard'
              : '',
            status: mapAEStatus(backendAE.status),
            createdAt: backendAE.createdAt,
            syntaxConfig: backendAE.abstractSyntaxes
              ? transformBackendSyntaxConfig(backendAE.abstractSyntaxes)
              : defaultSyntaxConfig,
            acceptedCallingAeTitles: backendAE.acceptedCallingAeTitles || [],
          })) || [],
      })) || [],
  }));
};
interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  is_verified: boolean;
  role: string;
  ae_title: string;
}

export default function AEListing({
  user,
  address,
}: {
  user: User;
  address: any;
}) {
  const { t } = useTranslation(['Organization']);
  const {
    data: devicesDataWithConnections,
    isLoading: isLoadingDevicesWithConnections,
    isError: isErrorDevicesWithConnections,
    refetch: refetchDevicesWithConnections,
  } = useGetDevicesWithConnections();

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const [sortConfig, setSortConfig] = useState({
    sort: 'id',
    direction: 'ASC' as 'ASC' | 'DESC',
  });

  // Device selection state - default to null for "All" devices
  const [selectedDeviceId, setSelectedDeviceId] = useState<number | null>(null);
  const [isDeviceDropdownOpen, setIsDeviceDropdownOpen] = useState(false);
  const [hasUserSelectedDevice, setHasUserSelectedDevice] = useState(false);

  const {
    data: applicationEntitiesData,
    isLoading: isLoadingApplicationEntities,
    refetch: refetchApplicationEntities,
  } = useGetApplicationEntitiesPageable({
    page: pagination.pageIndex,
    size: pagination.pageSize,
    sort: sortConfig.sort,
    direction: sortConfig.direction,
    deviceId: selectedDeviceId,
  });

  const createDeviceWithAutoTls = useCreateDeviceWithTls();
  const createDeviceWithPemTls = useCreateDeviceWithPemTls();
  const createDeviceWithPfxTls = useCreateDeviceWithPfxTls();
  const startDeviceMutation = useStartDevice();
  const stopDeviceMutation = useStopDevice();
  const restartDeviceMutation = useRestartDevice();
  const deleteDeviceMutation = useDeleteDevice();
  const deleteConnectionMutation = useDeleteConnection();
  const deleteApplicationEntityMutation = useDeleteApplicationEntity();
  const startApplicationEntityMutation = useStartApplicationEntity();
  const stopApplicationEntityMutation = useStopApplicationEntity();
  const updateDeviceMutation = useUpdateDevice();

  const [transformedDevices, setTransformedDevices] = useState<Device[]>([]);
  const { can } = usePermissions();
  const canManage = can('pacs_device.manage');

  const devices = devicesDataWithConnections || [];

  const selectedDevice = devices.find(
    (device: any) => device.id === selectedDeviceId
  );
  const selectedConnection = selectedDevice?.connections?.[0];

  // Handle device selection change
  const handleDeviceChange = (deviceId: number | null) => {
    setSelectedDeviceId(deviceId);
    setHasUserSelectedDevice(true);
    setIsDeviceDropdownOpen(false);
    setPagination({ pageIndex: 0, pageSize: 10 });
  };

  useEffect(() => {
    const result = transformBackendDevices(devicesDataWithConnections || []);
    setTransformedDevices(result);
  }, [devicesDataWithConnections]);

  useEffect(() => {
    if (
      devicesDataWithConnections &&
      devicesDataWithConnections.length > 0 &&
      !hasUserSelectedDevice
    ) {
      const deviceWithId1 = devicesDataWithConnections.find(
        (device: any) => device.id === 1
      );
      if (deviceWithId1 && selectedDeviceId === null) {
        setSelectedDeviceId(1);
      }
    }
  }, [devicesDataWithConnections, selectedDeviceId, hasUserSelectedDevice]);

  const {
    handleConfigureAE,
    updateAESyntaxConfig,
    updateAcceptedCallingAETitles,
  } = useDeviceManagement(transformedDevices);

  const handleDeviceCreation = async (deviceData: Record<string, unknown>) => {
    try {
      let response;

      if (deviceData.certificateOption === 'upload-pfx' && deviceData.pfxFile) {
        const { certificateOption, ...pfxDeviceData } = deviceData;
        response = await createDeviceWithPfxTls.mutateAsync(
          pfxDeviceData as any
        );
      } else if (
        deviceData.certificateOption === 'upload-pem' &&
        deviceData.certificatePem &&
        deviceData.privateKeyPem
      ) {
        const { certificateOption, ...pemDeviceData } = deviceData;

        const cleanPemData = {
          // Required device fields
          deviceName: pemDeviceData.deviceName,
          hostname: pemDeviceData.hostname,
          port: pemDeviceData.port,

          // Optional device fields
          ...(pemDeviceData.description && {
            description: pemDeviceData.description,
          }),
          ...(pemDeviceData.manufacturer && {
            manufacturer: pemDeviceData.manufacturer,
          }),
          ...(pemDeviceData.model && { model: pemDeviceData.model }),
          ...(pemDeviceData.stationName && {
            stationName: pemDeviceData.stationName,
          }),
          ...(pemDeviceData.institutionName && {
            institutionName: pemDeviceData.institutionName,
          }),
          ...(pemDeviceData.institutionAddress && {
            institutionAddress: pemDeviceData.institutionAddress,
          }),
          ...(pemDeviceData.institutionalDepartmentName && {
            institutionalDepartmentName:
              pemDeviceData.institutionalDepartmentName,
          }),

          // Connection fields
          ...(pemDeviceData.connectionName && {
            connectionName: pemDeviceData.connectionName,
          }),
          ...(pemDeviceData.connectionDescription && {
            connectionDescription: pemDeviceData.connectionDescription,
          }),

          // Timeout fields
          maxOpsInvoked: pemDeviceData.maxOpsInvoked || 100,
          maxOpsPerformed: pemDeviceData.maxOpsPerformed || 100,
          connectTimeout: pemDeviceData.connectTimeout || 30000,
          requestTimeout: pemDeviceData.requestTimeout || 30000,
          acceptTimeout: pemDeviceData.acceptTimeout || 30000,
          releaseTimeout: pemDeviceData.releaseTimeout || 5000,
          responseTimeout: pemDeviceData.responseTimeout || 60000,

          // TLS fields
          clientAuthRequired: pemDeviceData.clientAuthRequired || true,
          connectionTlsEnabled:
            pemDeviceData.connectionTlsEnabled !== undefined
              ? pemDeviceData.connectionTlsEnabled
              : true,
          connectionTlsNeedClientAuth:
            pemDeviceData.connectionTlsNeedClientAuth !== undefined
              ? pemDeviceData.connectionTlsNeedClientAuth
              : true,

          // PEM certificate fields
          certificatePem: pemDeviceData.certificatePem,
          privateKeyPem: pemDeviceData.privateKeyPem,
          ...(pemDeviceData.caCertificatePem && {
            caCertificatePem: pemDeviceData.caCertificatePem,
          }),
        };

        response = await createDeviceWithPemTls.mutateAsync(
          cleanPemData as any
        );
      } else {
        // Use auto-TLS for default case
        const {
          certificateOption,
          certificatePem,
          privateKeyPem,
          caCertificatePem,
          ...autoTlsDeviceData
        } = deviceData;
        response = await createDeviceWithAutoTls.mutateAsync(
          autoTlsDeviceData as any
        );
      }

      if (response?.success || response?.device) {
        await refetchDevicesWithConnections();

        setDeviceCreationDialog(false);
        return response;
      } else {
        throw new Error(response?.message || 'Failed to create device');
      }
    } catch (error) {
      console.error('Error creating device:', error);

      throw error;
    }
  };

  const handleEnhancedDeviceAction = async (
    deviceId: string,
    action: 'start' | 'stop' | 'restart'
  ) => {
    try {
      let response;
      switch (action) {
        case 'start':
          response = await startDeviceMutation.mutateAsync(parseInt(deviceId));
          break;
        case 'stop':
          response = await stopDeviceMutation.mutateAsync(parseInt(deviceId));
          break;
        case 'restart':
          response = await restartDeviceMutation.mutateAsync(
            parseInt(deviceId)
          );
          break;
        default:
          throw new Error(`Unknown action: ${action}`);
      }

      if (response?.success || response?.message) {
        if (response.message) {
          message.success(response.message);
        } else {
          messageI18n.success('devices.deviceActionSuccess', {
            action: `${action}ed`,
          });
        }
        await refetchDevicesWithConnections();
        await refetchApplicationEntities();
      } else {
        throw new Error(response?.message || `Failed to ${action} device`);
      }
    } catch (error: unknown) {
      console.error(`Error ${action}ing device:`, error);

      let errorMessage = tToast('common.genericError');

      const axiosError = error as {
        response?: {
          data?: {
            message?: string;
            details?: { error?: string } | string;
            error?: string;
          };
        };
      };
      if (axiosError?.response?.data) {
        const errorData = axiosError.response.data;

        if (errorData.message) {
          errorMessage = errorData.message;

          const errorDetails = errorData.details || errorData.error;
          if (errorDetails) {
            if (
              typeof errorDetails === 'object' &&
              errorDetails.error &&
              errorDetails.error.includes('already running')
            ) {
              errorMessage = `Device is already running`;
            } else if (
              typeof errorDetails === 'object' &&
              errorDetails.error &&
              errorDetails.error.includes('not found')
            ) {
              errorMessage = `Device not found`;
            } else if (
              typeof errorDetails === 'object' &&
              errorDetails.error &&
              errorDetails.error.includes('validation')
            ) {
              errorMessage = `Device validation failed: ${errorDetails.error}`;
            }
          }
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      }

      message.error(errorMessage);
    }
  };

  const handleEnhancedDeviceDelete = async (deviceId: string) => {
    const deviceIdNumber = parseInt(deviceId, 10);

    try {
      await deleteDeviceMutation.mutateAsync(deviceIdNumber);
      messageI18n.success('devices.deviceDeleted');
      await refetchDevicesWithConnections();
    } catch (error: any) {
      console.error('Error deleting device:', error);

      let errorMessage = tToast('common.genericError');

      const axiosError = error as any;
      if (axiosError?.response?.data) {
        const errorData = axiosError.response.data;

        if (errorData.message) {
          errorMessage = errorData.message;

          const errorDetails = errorData.details || errorData.error;
          if (errorDetails) {
            if (
              errorDetails.error &&
              errorDetails.error.includes('not found')
            ) {
              errorMessage = 'Device not found';
            } else if (
              errorDetails.error &&
              errorDetails.error.includes('in use')
            ) {
              errorMessage = 'Cannot delete device: Device is currently in use';
            }
          }
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      }

      message.error(errorMessage);
    }
  };

  const handleEnhancedConnectionDelete = async (
    deviceId: string,
    connectionId: string
  ) => {
    try {
      const device = transformedDevices.find((d) => d.id === deviceId);
      if (device && device.status === 'active') {
        messageI18n.error('devices.cannotDeleteConnectionDeviceStopped');
        return;
      }

      await deleteConnectionMutation.mutateAsync({
        deviceId: parseInt(deviceId, 10),
        connectionId: parseInt(connectionId, 10),
      });

      messageI18n.success('devices.connectionDeleted');
      await refetchDevicesWithConnections();
    } catch (error: any) {
      console.error('Error deleting connection:', error);

      let errorMessage = tToast('common.genericError');

      const axiosError = error as any;
      if (axiosError?.response?.data) {
        const errorData = axiosError.response.data;

        if (errorData.message) {
          errorMessage = errorData.message;

          const errorDetails = errorData.details || errorData.error;
          if (errorDetails) {
            if (
              errorDetails.error &&
              errorDetails.error.includes('not found')
            ) {
              errorMessage = 'Connection not found';
            } else if (
              errorDetails.error &&
              errorDetails.error.includes('in use')
            ) {
              errorMessage =
                'Cannot delete connection: Connection is currently in use';
            }
          }
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      }

      message.error(errorMessage);
    }
  };

  const handleEnhancedAEUpdate = async (
    aeId: string,
    updateData: {
      aeTitle: string;
      description: string;
      dicomType: 'standard' | 'blind';
      transferCapabilities: Array<{
        sopClass: string;
        role: 'SCP' | 'SCU';
        transferSyntaxes: string; // Can be empty string "" to delete this SOP class
        commonName?: string;
        enabled?: boolean;
        priority?: number;
      }>;
    }
  ) => {
    try {
      // Always send ALL SOP classes in transferCapabilities array
      // Empty transferSyntaxes "" = delete this SOP class
      // Non-empty transferSyntaxes = keep/update this SOP class with these transfer syntaxes
      const requestBody = {
        aeTitle: updateData.aeTitle,
        description: updateData.description,
        aeType: updateData.dicomType === 'blind' ? 'BLIND' : 'OB',
        transferCapabilities: updateData.transferCapabilities, // Always contains ALL SOP classes
      };

      console.log(
        'Sending comprehensive AE update with ALL SOP classes:',
        requestBody
      );

      const response = await fetch(`/api/v1/pacs/aes/${aeId}/comprehensive`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();

      if (result.status === 'success') {
        console.log('AE updated successfully:', result);
        await handleRefresh();
        return result;
      } else {
        console.error('Failed to update AE:', result);
        throw new Error(result.message || 'Failed to update AE');
      }
    } catch (error) {
      console.error('Error updating AE:', error);
      throw error;
    }
  };

  const handleEnhancedAEDelete = async (deviceId: string, aeId: string) => {
    const deviceIdNumber = parseInt(deviceId, 10);
    const aeIdNumber = parseInt(aeId, 10);

    try {
      await deleteApplicationEntityMutation.mutateAsync({
        deviceId: deviceIdNumber,
        aeId: aeIdNumber,
      });
      messageI18n.success('devices.aeDeleted');
      await refetchDevicesWithConnections();
    } catch (error: any) {
      console.error('Error deleting application entity:', error);

      let errorMessage = tToast('common.genericError');

      const axiosError = error as any;
      if (axiosError?.response?.data) {
        const errorData = axiosError.response.data;

        if (errorData.message) {
          errorMessage = errorData.message;

          const errorDetails = errorData.details || errorData.error;
          if (errorDetails) {
            if (
              errorDetails.error &&
              errorDetails.error.includes('not found')
            ) {
              errorMessage = 'Application entity not found';
            } else if (
              errorDetails.error &&
              errorDetails.error.includes('in use')
            ) {
              errorMessage =
                'Cannot delete application entity: Entity is currently in use';
            }
          }
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      }

      message.error(errorMessage);
    }
  };

  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    type: 'device' | 'connection' | 'ae';
    item:
      | Device
      | Connection
      | ApplicationEntity
      | { connection: Connection; deviceId: string }
      | null;
  }>({
    open: false,
    type: 'device',
    item: null,
  });

  const [deviceCreationDialog, setDeviceCreationDialog] = useState(false);
  const [cloneDeviceDialog, setCloneDeviceDialog] = useState<{
    open: boolean;
    device: Device | null;
  }>({
    open: false,
    device: null,
  });
  const [aeViewDialog, setAeViewDialog] = useState<{
    open: boolean;
    ae: ApplicationEntity | null;
    device: Device | null;
    connection: Connection | null;
  }>({
    open: false,
    ae: null,
    device: null,
    connection: null,
  });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>(
    {}
  );
  const [configureDeviceDialog, setConfigureDeviceDialog] = useState<{
    open: boolean;
    device: any | null;
  }>({
    open: false,
    device: null,
  });
  const [certificateDialog, setCertificateDialog] = useState<{
    open: boolean;
    device: any | null;
  }>({
    open: false,
    device: null,
  });

  const [configureAeDialog, setConfigureAeDialog] = useState<{
    open: boolean;
    ae: ApplicationEntity | null;
    device: Device | null;
    connection: Connection | null;
  }>({
    open: false,
    ae: null,
    device: null,
    connection: null,
  });
  const [acceptedCallingAETitlesDialog, setAcceptedCallingAETitlesDialog] =
    useState<{
      open: boolean;
      ae: ApplicationEntity | null;
      device: Device | null;
      connection: Connection | null;
    }>({
      open: false,
      ae: null,
      device: null,
      connection: null,
    });

  // const filteredDevices = transformedDevices?.filter(
  //   (device) =>
  //     device?.name?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
  //     device?.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //     device?.connections?.some((conn) =>
  //       conn.applicationEntities.some((ae) =>
  //         ae.aeTitle.toLowerCase().includes(searchTerm.toLowerCase())
  //       )
  //     )
  // );

  useEffect(() => {
    const interval = setInterval(() => {}, 30000);

    return () => clearInterval(interval);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isDeviceDropdownOpen) {
        const target = event.target as HTMLElement;
        if (!target.closest('.device-dropdown')) {
          setIsDeviceDropdownOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDeviceDropdownOpen]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetchDevicesWithConnections();
      await refetchApplicationEntities();
    } catch (error) {
      console.error('Error refreshing devices:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const toggleDropdown = (id: string) => {
    setOpenDropdowns((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleCertificateBundleDownload = async (deviceId: number) => {
    try {
      await downloadCertificateBundle(deviceId);
      messageI18n.success('devices.certificateBundleDownloaded');
    } catch (error: any) {
      console.error('Failed to download certificate bundle:', error);
      let errorMessage = tToast('devices.certificateDownloadFailed');
      if (error?.message) {
        errorMessage = error.message;
      }
      message.error(errorMessage);
    }
  };

  const handleIndividualCertificatesDownload = async (deviceId: number) => {
    let successCount = 0;
    let errorCount = 0;
    const errors: string[] = [];

    const downloads = [
      {
        name: 'Client Key',
        downloadFn: () => downloadClientKey(deviceId),
      },
      {
        name: 'Client Certificate',
        downloadFn: () => downloadClientCertificate(deviceId),
      },
      {
        name: 'CA Certificate',
        downloadFn: () => downloadCACertificate(deviceId),
      },
    ];

    for (const download of downloads) {
      try {
        await download.downloadFn();
        successCount++;
      } catch (error: any) {
        errorCount++;
        const errorMessage =
          error?.message || `Failed to download ${download.name}`;
        errors.push(`${download.name}: ${errorMessage}`);
        console.error(`Failed to download ${download.name}:`, error);
      }
    }

    if (successCount === 3) {
      messageI18n.success('devices.allCertificatesDownloaded');
    } else if (successCount > 0) {
      messageI18n.warning('devices.certificatesPartialDownload', {
        successCount,
        errorCount,
      });
    } else {
      messageI18n.error('devices.certificateDownloadFailed');
    }

    if (errors.length > 0) {
      console.error('Certificate download errors:', errors);
    }
  };

  if (isLoadingDevicesWithConnections) {
    return (
      <div className="w-full mx-auto">
        <div className="rounded-xl border border-slate-200 bg-white py-16 text-center dark:border-gray-700 dark:bg-gray-800">
          <RefreshCw className="h-10 w-10 text-blue-500 mx-auto mb-4 animate-spin" />
          <h3 className="text-base font-medium text-slate-900 dark:text-white mb-1">
            {t('pacsTab.loadingDevices')}
          </h3>
          <p className="text-sm text-slate-500 dark:text-gray-400">
            {t('pacsTab.loadingWait')}
          </p>
        </div>
      </div>
    );
  }

  if (isErrorDevicesWithConnections) {
    return (
      <div className="w-full mx-auto">
        <div className="rounded-xl border border-slate-200 bg-white py-16 text-center dark:border-gray-700 dark:bg-gray-800">
          <Monitor className="h-10 w-10 text-red-400 mx-auto mb-4" />
          <h3 className="text-base font-medium text-slate-900 dark:text-white mb-1">
            {t('pacsTab.errorTitle')}
          </h3>
          <p className="text-sm text-slate-500 dark:text-gray-400 mb-4">
            {t('pacsTab.errorDescription')}
          </p>
          <Button onClick={handleRefresh} variant="outline" size="sm">
            <RefreshCw className="h-3.5 w-3.5 mr-2" />
            {t('pacsTab.refresh')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full mx-auto">
      {/* AE Listing Header */}
      <div>
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="min-w-0 flex-1">
              <h1 className="text-lg sm:text-xl font-semibold text-title-page-color-100 dark:text-white flex items-center gap-2">
                <Monitor className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 flex-shrink-0" />
                <span className="truncate">{t('pacsTab.medicalMachines')}</span>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500 dark:bg-gray-700 dark:text-gray-400">
                  {devices.length}
                </span>
              </h1>
              <p className="text-xs sm:text-sm text-title-page-color-300 dark:text-gray-400 mt-1">
                {t('pacsTab.medicalMachinesDescription')}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-2">
              {/* Device Dropdown */}
              <div className="relative device-dropdown">
                <Button
                  onClick={() => setIsDeviceDropdownOpen(!isDeviceDropdownOpen)}
                  variant="outline"
                  size="sm"
                  className="text-sm sm:text-base min-w-[200px] justify-between"
                >
                  <span className="truncate">
                    {selectedDeviceId === null
                      ? t('pacsTab.allDevices')
                      : selectedDevice?.deviceName || t('pacsTab.selectDevice')}
                  </span>
                  <ChevronDown className="h-4 w-4 ml-2 flex-shrink-0" />
                </Button>

                {isDeviceDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                    {/* All Devices Option */}
                    <button
                      onClick={() => handleDeviceChange(null)}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                        selectedDeviceId === null
                          ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                          : 'text-gray-900 dark:text-gray-100'
                      }`}
                    >
                      <div className="truncate font-medium">{t('pacsTab.allDevices')}</div>
                      <div className="truncate text-xs text-gray-500 dark:text-gray-400">
                        {t('pacsTab.allDevicesHint')}
                      </div>
                    </button>

                    {devices.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                        {t('pacsTab.noDevices')}
                      </div>
                    ) : (
                      devices.map((device: any) => (
                        <button
                          key={device.id}
                          onClick={() => handleDeviceChange(device.id)}
                          className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                            selectedDeviceId === device.id
                              ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                              : 'text-gray-900 dark:text-gray-100'
                          }`}
                        >
                          <div className="truncate font-medium">
                            {device.deviceName}
                          </div>
                          {device.description && (
                            <div className="truncate text-xs text-gray-500 dark:text-gray-400">
                              {device.description}
                            </div>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
              <Button
                onClick={() => setDeviceCreationDialog(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white text-sm sm:text-base"
                disabled={!canManage}
                size="sm"
              >
                <Plus className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                <span className="hidden sm:inline">{t('pacsTab.createDevice')}</span>
              </Button>
              <Button
                onClick={handleRefresh}
                disabled={isRefreshing}
                variant="outline"
                size="sm"
                className="text-sm sm:text-base"
              >
                <RefreshCw
                  className={`h-3 w-3 sm:h-4 sm:w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`}
                />
                {t('pacsTab.refresh')}
              </Button>
            </div>
          </div>
        </div>

        <div>
          {/* Device Info Card */}
          {selectedDeviceId === null ? (
            <div className="mb-6 rounded-xl border border-blue-100 bg-blue-50/60 p-4 dark:border-blue-900/40 dark:bg-blue-900/10">
              <div className="flex items-center gap-4">
                <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30">
                  <Monitor className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                      {t('pacsTab.allDevices')}
                    </h3>
                    <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                      {devices.length}{' '}
                      {devices.length === 1
                        ? t('pacsTab.device')
                        : t('pacsTab.devices')}
                    </span>
                  </div>
                  <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-300">
                    {t('pacsTab.viewingAllAe')}
                  </p>
                </div>
              </div>
            </div>
          ) : selectedDevice && selectedConnection ? (
            <div className="mb-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200">
              <div className="p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                      <Server className="h-7 w-7 text-white" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white truncate">
                          {selectedDevice.deviceName}
                        </h3>
                        <div className="flex items-center gap-2">
                          <div
                            className={`w-2 h-2 rounded-full ${
                              selectedDevice.status === 'ACTIVE'
                                ? 'bg-green-500 animate-pulse'
                                : 'bg-gray-400'
                            }`}
                          />
                          <span
                            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                              selectedDevice.status === 'ACTIVE'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {selectedDevice.status === 'ACTIVE'
                              ? t('pacsTab.status.active')
                              : t('pacsTab.status.inactive')}
                          </span>
                        </div>
                      </div>
                      {/* Device Actions Dropdown */}
                      <Dropdown
                        trigger={
                          <Button variant="ghost" size="sm">
                            <Settings className="h-4 w-4" />
                          </Button>
                        }
                        isOpen={
                          openDropdowns[`device-${selectedDevice.id}`] || false
                        }
                        onToggle={() =>
                          toggleDropdown(`device-${selectedDevice.id}`)
                        }
                      >
                        {selectedDevice.status === 'INACTIVE' && (
                          <DropdownItem
                            onClick={() => {
                              handleEnhancedDeviceAction(
                                selectedDevice.id.toString(),
                                'start'
                              );
                              toggleDropdown(`device-${selectedDevice.id}`);
                            }}
                            className={
                              selectedDevice.connections?.some(
                                (conn: any) =>
                                  conn.applicationEntities?.length > 0
                              )
                                ? 'text-green-600'
                                : 'text-gray-400'
                            }
                            disabled={
                              startDeviceMutation.isPending || !canManage
                            }
                          >
                            <div className="flex items-center gap-2">
                              <Power className="h-4 w-4" />
                              {startDeviceMutation.isPending
                                ? t('pacsTab.starting')
                                : t('pacsTab.startDevice')}
                            </div>
                          </DropdownItem>
                        )}
                        {selectedDevice.status === 'ACTIVE' && (
                          <>
                            <DropdownItem
                              onClick={() => {
                                handleEnhancedDeviceAction(
                                  selectedDevice.id.toString(),
                                  'stop'
                                );
                                toggleDropdown(`device-${selectedDevice.id}`);
                              }}
                              className="text-red-600"
                              disabled={
                                stopDeviceMutation.isPending || !canManage
                              }
                            >
                              <div className="flex items-center gap-2">
                                <PowerOff className="h-4 w-4" />
                                {stopDeviceMutation.isPending
                                  ? t('pacsTab.stopping')
                                  : t('pacsTab.stopDevice')}
                              </div>
                            </DropdownItem>
                            <DropdownItem
                              onClick={() => {
                                handleEnhancedDeviceAction(
                                  selectedDevice.id.toString(),
                                  'restart'
                                );
                                toggleDropdown(`device-${selectedDevice.id}`);
                              }}
                              className="text-blue-600"
                              disabled={
                                restartDeviceMutation.isPending || !canManage
                              }
                            >
                              <div className="flex items-center gap-2">
                                <RotateCcw className="h-4 w-4" />
                                {restartDeviceMutation.isPending
                                  ? t('pacsTab.restarting')
                                  : t('pacsTab.restartDevice')}
                              </div>
                            </DropdownItem>
                          </>
                        )}
                        {/* <DropdownItem
                        onClick={() => {
                          setConfigureDeviceDialog({
                            open: true,
                            device: selectedDevice,
                          });
                          toggleDropdown(`device-${selectedDevice.id}`);
                        }}
                        className="text-slate-600"
                        disabled={!canManage}
                      >
                        <div className="flex items-center gap-2">
                          <Settings className="h-4 w-4" />
                          Configure Device
                        </div>
                      </DropdownItem> */}
                        {selectedDevice.status === 'INACTIVE' && (
                          <DropdownItem
                            onClick={() => {
                              setDeleteDialog({
                                open: true,
                                type: 'device',
                                item: transformedDevices.find(
                                  (d) => d.id === selectedDevice.id.toString()
                                ),
                              });
                              toggleDropdown(`device-${selectedDevice.id}`);
                            }}
                            className="text-red-600"
                            disabled={
                              deleteDeviceMutation.isPending || !canManage
                            }
                          >
                            <div className="flex items-center gap-2">
                              <Trash2 className="h-4 w-4" />
                              {deleteDeviceMutation.isPending
                                ? t('pacsTab.deleting')
                                : t('pacsTab.deleteDevice')}
                            </div>
                          </DropdownItem>
                        )}
                      </Dropdown>
                    </div>
                    {selectedDevice.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
                        {selectedDevice.description}
                      </p>
                    )}
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mt-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                            <Wifi className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              {t('pacsTab.connection')}
                            </p>
                            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate mt-1">
                              {selectedConnection.connectionName}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                            <Server className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              {t('pacsTab.address')}
                            </p>
                            <p className="text-sm font-semibold text-gray-900 dark:text-white mt-1">
                              {address?.data?.static_ips &&
                              address?.data?.static_ips.length > 0
                                ? address?.data?.static_ips
                                    .map((ip: any) => `${ip?.address}`)
                                    .join(', ')
                                : `${selectedConnection?.hostname}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-8 h-8 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
                            <Cable className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              {t('pacsTab.port')}
                            </p>
                            <p className="text-sm font-semibold text-gray-900 dark:text-white mt-1">
                              {selectedConnection.port}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div
                            className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${
                              selectedConnection.tlsEnabled
                                ? 'bg-green-100 dark:bg-green-900/30'
                                : 'bg-red-100 dark:bg-red-900/30'
                            }`}
                          >
                            <div
                              className={`w-3 h-3 rounded-full ${
                                selectedConnection.tlsEnabled
                                  ? 'bg-green-500'
                                  : 'bg-red-500'
                              }`}
                            />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              {t('pacsTab.security')}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <p
                                className={`text-sm font-semibold ${
                                  selectedConnection.tlsEnabled
                                    ? 'text-green-700 dark:text-green-300'
                                    : 'text-red-700 dark:text-red-300'
                                }`}
                              >
                                {selectedConnection.tlsEnabled
                                  ? t('pacsTab.tlsEnabled')
                                  : t('pacsTab.noTls')}
                              </p>
                            </div>
                            {selectedConnection.tlsEnabled && (
                              <div className="flex gap-2 mt-2">
                                <Button
                                  onClick={async () => {
                                    await handleIndividualCertificatesDownload(
                                      selectedDevice.id
                                    );
                                  }}
                                  variant="outline"
                                  size="sm"
                                  className="text-xs px-3 py-1.5 h-7 bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-700 dark:hover:bg-emerald-900/40 font-medium transition-all duration-200"
                                  title={t('devices.modals.downloadCertificates')}
                                >
                                  <Upload className="h-3 w-3 mr-1.5" />
                                  {t('devices.modals.downloadCertificates')}
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {/* Application Entities Table or Empty State */}
          {isLoadingApplicationEntities ? (
            <div className="text-center py-12 bg-gray-50 dark:bg-gray-900/50 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700">
              <RefreshCw className="h-12 w-12 text-blue-500 dark:text-blue-400 mx-auto mb-4 animate-spin" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {t('pacsTab.loadingAe')}
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                {selectedDeviceId === null
                  ? t('pacsTab.fetchingAllAe')
                  : t('pacsTab.loadingAeForDevice', {
                      name: selectedDevice?.deviceName,
                    })}
              </p>
            </div>
          ) : (
            <ApplicationEntitiesTable
              devicesDataWithConnections={devicesDataWithConnections}
              isLoadingDevicesWithConnections={isLoadingDevicesWithConnections}
              address={address}
              applicationEntitiesData={applicationEntitiesData}
              isLoadingApplicationEntities={isLoadingApplicationEntities}
              refetchApplicationEntities={refetchApplicationEntities}
              refetchDevicesWithConnections={refetchDevicesWithConnections}
              pagination={pagination}
              setPagination={setPagination}
              sortConfig={sortConfig}
              setSortConfig={setSortConfig}
              selectedDeviceId={selectedDeviceId}
            />
          )}
        </div>
      </div>

      {/* Device Creation Modal */}
      <Modal
        isOpen={deviceCreationDialog}
        onClose={() => setDeviceCreationDialog(false)}
        size="4xl"
        className="bg-card-color-100 dark:bg-gray-800"
      >
        <Modal.Header
          title={t('devices.modals.createDevice')}
          onClose={() => setDeviceCreationDialog(false)}
        />
        <Modal.Body className="max-h-[80vh]">
          <DeviceCreationOptimalForm
            isModal={true}
            onCancel={() => setDeviceCreationDialog(false)}
            onDeviceCreated={handleDeviceCreation}
            devicesData={devicesDataWithConnections}
            isLoadingDevices={isLoadingDevicesWithConnections}
            address={address}
            refetchApplicationEntities={refetchApplicationEntities}
            refetchDevicesWithConnections={refetchDevicesWithConnections}
            selectedDeviceId={selectedDeviceId}
            isAllDevicesSelected={selectedDeviceId === null}
          />
        </Modal.Body>
      </Modal>

      {/* Configure Application Entity Modal */}
      <ConfigureAEModal
        isOpen={configureAeDialog.open}
        onClose={() =>
          setConfigureAeDialog({
            open: false,
            ae: null,
            device: null,
            connection: null,
          })
        }
        ae={configureAeDialog.ae}
        device={configureAeDialog.device}
        connection={configureAeDialog.connection}
        onUpdate={handleConfigureAE}
        onSyntaxUpdate={updateAESyntaxConfig}
      />

      {/* Configure Device Modal */}
      {/* <ConfigureDeviceModal
        isOpen={configureDeviceDialog.open}
        onClose={() => setConfigureDeviceDialog({ open: false, device: null })}
        device={configureDeviceDialog.device}
        onUpdate={async (deviceId: string, formData: any) => {
          try {
            const response = await updateDeviceMutation.mutateAsync({
              deviceId: parseInt(deviceId, 10),
              deviceData: formData,
            });

            if (response?.status === 'success' || response?.data) {
              messageI18n.success('devices.deviceUpdated');
              await refetchDevicesWithConnections();
              await refetchApplicationEntities();
            } else {
              throw new Error(response?.message || 'Failed to update device');
            }
          } catch (error: unknown) {
            console.error('Error updating device:', error);
            let errorMessage = tToast('common.genericError');
            const axiosError = error as {
              response?: {
                data?: {
                  message?: string;
                  details?: { error?: string } | string;
                  error?: string;
                };
              };
            };
            if (axiosError?.response?.data) {
              const errorData = axiosError.response.data;
              if (errorData.message) {
                errorMessage = errorData.message;
              } else if (errorData.error) {
                errorMessage = errorData.error;
              }
            }
            message.error(errorMessage);
            throw error;
          }
        }}
      /> */}

      {/* Certificate Management Modal */}
      <CertificateManagementModal
        isOpen={certificateDialog.open}
        onClose={() => setCertificateDialog({ open: false, device: null })}
        device={certificateDialog.device}
        onUpdate={(device, option) => {
          // Handle certificate update
        }}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteDialog.open}
        onClose={() =>
          setDeleteDialog({ open: false, type: 'device', item: null })
        }
        type={deleteDialog.type}
        item={deleteDialog.item}
        onDelete={() => {
          if (
            deleteDialog.type === 'connection' &&
            deleteDialog.item &&
            'deviceId' in deleteDialog.item
          ) {
            handleEnhancedConnectionDelete(
              deleteDialog.item.deviceId,
              deleteDialog.item.connection.id
            );
          } else if (
            deleteDialog.type === 'device' &&
            deleteDialog.item &&
            'id' in deleteDialog.item
          ) {
            handleEnhancedDeviceDelete(deleteDialog.item.id);
          } else if (
            deleteDialog.type === 'ae' &&
            deleteDialog.item &&
            'id' in deleteDialog.item
          ) {
            const aeItem = deleteDialog.item as ApplicationEntity;
            const deviceId = transformedDevices.find((device) =>
              device.connections.some((conn) =>
                conn.applicationEntities.some((ae) => ae.id === aeItem.id)
              )
            )?.id;
            if (deviceId) {
              handleEnhancedAEDelete(deviceId, aeItem.id);
            }
          }
          setDeleteDialog({ open: false, type: 'device', item: null });
        }}
      />

      {/* Application Entity View Modal */}
      <AEViewModal
        isOpen={aeViewDialog.open}
        onClose={() =>
          setAeViewDialog({
            open: false,
            ae: null,
            device: null,
            connection: null,
          })
        }
        ae={aeViewDialog.ae}
        device={aeViewDialog.device}
        connection={aeViewDialog.connection}
      />

      {/* Accepted Calling AE Titles Modal */}
      <AcceptedCallingAETitlesModal
        isOpen={acceptedCallingAETitlesDialog.open}
        onClose={() =>
          setAcceptedCallingAETitlesDialog({
            open: false,
            ae: null,
            device: null,
            connection: null,
          })
        }
        ae={acceptedCallingAETitlesDialog.ae}
        device={acceptedCallingAETitlesDialog.device}
        connection={acceptedCallingAETitlesDialog.connection}
        onUpdate={updateAcceptedCallingAETitles}
      />
    </div>
  );
}
